import React, { useState, useMemo, useCallback, memo } from 'react';
import { motion } from 'framer-motion';
import { StarIcon } from '@heroicons/react/24/solid';
import { StarIcon as StarOutlineIcon } from '@heroicons/react/24/outline';

// Memoized Star Button Component
const StarButton = memo(({ 
  starIndex, 
  isFilled, 
  isHovered, 
  currentSize, 
  onClick, 
  onMouseEnter 
}) => {
  return (
    <motion.button
      type="button"
      whileHover={{ scale: 1.1 }}
      whileTap={{ scale: 0.9 }}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      style={{
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        padding: '2px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      {isFilled || isHovered ? (
        <StarIcon style={{
          ...currentSize.star,
          color: isHovered ? '#fcd34d' : '#fbbf24',
          transition: 'all 0.2s ease'
        }} />
      ) : (
        <StarOutlineIcon style={{
          ...currentSize.star,
          color: '#d1d5db',
          transition: 'all 0.2s ease'
        }} />
      )}
    </motion.button>
  );
}, (prevProps, nextProps) => {
  return (
    prevProps.isFilled === nextProps.isFilled &&
    prevProps.isHovered === nextProps.isHovered &&
    prevProps.starIndex === nextProps.starIndex
  );
});

StarButton.displayName = 'StarButton';

// Memoized Static Star Component
const StaticStar = memo(({ starIndex, rating, currentSize }) => {
  return (
    <StarIcon
      style={{
        ...currentSize.star,
        color: starIndex < rating ? '#fbbf24' : '#e5e7eb'
      }}
    />
  );
}, (prevProps, nextProps) => {
  const prevFilled = prevProps.starIndex < prevProps.rating;
  const nextFilled = nextProps.starIndex < nextProps.rating;
  return prevFilled === nextFilled && prevProps.starIndex === nextProps.starIndex;
});

StaticStar.displayName = 'StaticStar';

const RatingDisplay = ({ 
  rating, 
  reviewCount, 
  size = 'default', 
  interactive = false,
  onChange,
  showText = true
}) => {
  const [hoverRating, setHoverRating] = useState(0);
  const [selectedRating, setSelectedRating] = useState(rating || 0);

  // Memoized size styles - only recalculates when size changes
  const currentSize = useMemo(() => {
    const sizes = {
      small: { 
        star: { height: '12px', width: '12px' }, 
        text: { fontSize: '12px' } 
      },
      default: { 
        star: { height: '16px', width: '16px' }, 
        text: { fontSize: '14px' } 
      },
      large: { 
        star: { height: '20px', width: '20px' }, 
        text: { fontSize: '16px' } 
      },
      xlarge: { 
        star: { height: '24px', width: '24px' }, 
        text: { fontSize: '18px' } 
      }
    };
    return sizes[size] || sizes.default;
  }, [size]);

  // Memoized star indices array
  const starIndices = useMemo(() => [0, 1, 2, 3, 4], []);

  // Optimized handlers with useCallback
  const handleStarClick = useCallback((starIndex) => {
    if (!interactive) return;
    
    const newRating = starIndex + 1;
    setSelectedRating(newRating);
    if (onChange) {
      onChange(newRating);
    }
  }, [interactive, onChange]);

  const handleStarHover = useCallback((starIndex) => {
    if (!interactive) return;
    setHoverRating(starIndex + 1);
  }, [interactive]);

  const handleMouseLeave = useCallback(() => {
    if (!interactive) return;
    setHoverRating(0);
  }, [interactive]);

  // Memoized display rating
  const displayRating = useMemo(() => {
    return interactive ? (hoverRating || selectedRating) : rating;
  }, [interactive, hoverRating, selectedRating, rating]);

  // Memoized formatted rating text
  const formattedRating = useMemo(() => {
    return rating?.toFixed(1);
  }, [rating]);

  // Memoized formatted review count
  const formattedReviewCount = useMemo(() => {
    return reviewCount ? reviewCount.toLocaleString() : null;
  }, [reviewCount]);

  // Interactive mode rendering
  if (interactive) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
      }}>
        <div 
          style={{ 
            display: 'flex', 
            alignItems: 'center',
            gap: '4px'
          }}
          onMouseLeave={handleMouseLeave}
        >
          {starIndices.map((starIndex) => {
            const isFilled = starIndex < displayRating;
            const isHovered = interactive && starIndex < hoverRating;
            
            return (
              <StarButton
                key={starIndex}
                starIndex={starIndex}
                isFilled={isFilled}
                isHovered={isHovered}
                currentSize={currentSize}
                onClick={() => handleStarClick(starIndex)}
                onMouseEnter={() => handleStarHover(starIndex)}
              />
            );
          })}
        </div>
        
        {showText && selectedRating > 0 && (
          <motion.span
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            style={{
              fontWeight: '600',
              color: '#111827',
              ...currentSize.text
            }}
          >
            {selectedRating}.0
          </motion.span>
        )}
      </div>
    );
  }

  // Static mode rendering (optimized)
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
        {starIndices.map((starIndex) => (
          <StaticStar
            key={starIndex}
            starIndex={starIndex}
            rating={rating}
            currentSize={currentSize}
          />
        ))}
      </div>
      
      {showText && (
        <>
          <span style={{
            fontWeight: '600',
            color: '#111827',
            ...currentSize.text
          }}>
            {formattedRating}
          </span>
          
          {formattedReviewCount && (
            <span style={{
              color: '#6b7280',
              ...currentSize.text
            }}>
              ({formattedReviewCount})
            </span>
          )}
        </>
      )}
    </div>
  );
};

export default memo(RatingDisplay, (prevProps, nextProps) => {
  // Custom comparison to prevent unnecessary re-renders
  return (
    prevProps.rating === nextProps.rating &&
    prevProps.reviewCount === nextProps.reviewCount &&
    prevProps.size === nextProps.size &&
    prevProps.interactive === nextProps.interactive &&
    prevProps.showText === nextProps.showText &&
    prevProps.onChange === nextProps.onChange
  );
});